import { SavedSession } from "../types";

const STORAGE_KEY = 'tradevision_sessions_v1';

// All session storage is now purely local (localStorage-only).

export const getSavedSessions = async (): Promise<SavedSession[]> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to load sessions", e);
    return [];
  }
};

export const saveSession = async (session: SavedSession): Promise<SavedSession[]> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const sessions: SavedSession[] = raw ? JSON.parse(raw) : [];
    const updated = [session, ...sessions];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error("Failed to save session", e);
    // On error, fall back to whatever we can load from localStorage
    return getSavedSessions();
  }
};

export const deleteSession = async (sessionId: string): Promise<SavedSession[]> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const sessions: SavedSession[] = raw ? JSON.parse(raw) : [];
    const updated = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error("Failed to delete session", e);
    return getSavedSessions();
  }
};

export const exportSessions = async () => {
  try {
    const sessions = await getSavedSessions();
    if (sessions.length === 0) {
        alert("No sessions to export.");
        return;
    }
    
    const dataStr = JSON.stringify(sessions, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `tradevision_backup_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Export failed", e);
    alert("Failed to export sessions.");
  }
};

export const importSessions = async (file: File): Promise<SavedSession[]> => {
  // Import logic remains local-only for safety.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        
        if (!Array.isArray(parsed)) throw new Error("Invalid format");
        
        // For now, we just return the parsed data. 
        // The App component will decide whether to save these to DB or Local
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsText(file);
  });
};